/* eslint-disable @typescript-eslint/no-use-before-define */
import * as React from 'react';
import { Button, Input, Modal, PhoneNumberInput, TextBox } from '../..';
import { showAlert, useAuth } from '../../../context/authContext';
import { handleCustomerAction } from '../../../context/miscContext';
import useFetchAsync from '../../../hooks/useFetchAsync';
import useValidator from '../../../hooks/useValidator';
import { CustomerFormDataType } from '../../Forms/CustomerForm/CustomerForm';
import { Option } from '../../Inputs/SelectInput/SelectInput';

type ModalProps = {
  onCloseModal: () => void;
  onSetAddedValue: (option: Option) => void;
  storeId: number;
};

function CustomerAdd({ onCloseModal, onSetAddedValue, storeId }: ModalProps) {
  const [, dispatch] = useAuth();
  const [account, setAccount] = React.useState({
    name: '',
    email: '',
    phoneNumber: '',
    address: '',
    notes: '',
    role: 'Customer',
  });
  const [errors, setErrors] = React.useState({
    name: false,
    email: false,
    phoneNumber: false,
  });
  const { data, status, error, run } = useFetchAsync();
  const [validator, showValidationMessage] = useValidator({});

  React.useEffect(() => {
    if (status === 'REJECTED' && error?.message) {
      showAlert(
        dispatch,
        {
          show: true,
          message:
            error?.message ||
            'Oops. Something unusual happened at our end. Please contact our support team!',
          type: 'ERROR',
        },
        { autoDismiss: true, time: 3200 },
      );
    }

    if (status === 'RESOLVED' && data) {
      const { customer } = data as any;

      if (customer) {
        onSetAddedValue({
          value: customer.customerId,
          label: customer.name,
        });
      }

      onCloseModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dispatch, error?.message, status]); // No need to call onSetAddedValue() and onCloseModal()

  const handleAccountInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;

    setAccount(fndAccount => ({
      ...fndAccount,
      [name]: value,
    }));
  };

  const handleCustomerSave = async () => {
    if (!validator.allValid()) {
      setErrors(errs => ({
        ...errs,
        name: !validator.fieldValid('name'),
        email: !validator.fieldValid('email'),
        phoneNumber: !validator.fieldValid('phoneNumber'),
      }));
      showValidationMessage(true);
      return;
    }

    handleCustomerAction(
      run,
      storeId,
      account as CustomerFormDataType,
      showFormActionAlert,
      'post',
    );
  };

  const showFormActionAlert = (message: string) => {
    showAlert(
      dispatch,
      {
        show: true,
        message,
        type: 'ERROR',
      },
      { autoDismiss: true, time: 3200 },
    );
  };

  return (
    <Modal onClose={onCloseModal} show onHide={() => false}>
      <div className="rounded-md bg-white tablet:w-[500px]">
        <form>
          <div className="flex flex-col p-5">
            <h3
              className="text-pos-black text-[20px] font-semibold mb-2
              inline-flex whitespace-pre"
            >
              Add New Customer
            </h3>
            <div className="text-pos-black text-sm mt-2 mb-2 font-normal space-y-4">
              <div>
                <label htmlFor="name" className="text-xbase block mb-1">
                  Name
                </label>
                <Input
                  name="name"
                  id="name"
                  value={account.name}
                  onChange={handleAccountInputChange}
                  onBlur={() => {
                    setErrors(errs => ({
                      ...errs,
                      name: !validator.check(account.name, 'required|min:2'),
                    }));
                  }}
                  error={errors.name}
                />
                {validator.message('name', account.name, 'required|min:2', {
                  messages: {
                    required: 'Full name is required.',
                  },
                  className:
                    'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                })}
              </div>
              <div className="flex flex-row gap-5">
                <div className="flex-1 w-full">
                  <label htmlFor="email" className="text-xbase block mb-1">
                    Email
                  </label>
                  <Input
                    id="email"
                    name="email"
                    value={account.email}
                    type="email"
                    autoComplete="username"
                    onChange={handleAccountInputChange}
                    onBlur={() => {
                      setErrors(errs => ({
                        ...errs,
                        email: !validator.check(account.email, 'email'),
                      }));
                    }}
                    error={errors.email}
                  />
                  {validator.message('email', account.email, 'email', {
                    className:
                      'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                  })}
                </div>

                <div className="w-[35%]">
                  <label
                    htmlFor="phoneNumber"
                    className="text-xbase block mb-1"
                  >
                    Phone
                  </label>
                  <PhoneNumberInput
                    id="phoneNumber"
                    name="phoneNumber"
                    defaultCountry="GH"
                    value={account.phoneNumber}
                    onChange={value =>
                      setAccount(fndAccount => ({
                        ...fndAccount,
                        phoneNumber: value,
                      }))
                    }
                    onBlur={() => {
                      setErrors(errs => ({
                        ...errs,
                        phoneNumber: !validator.check(
                          account.phoneNumber,
                          'phone',
                        ),
                      }));
                    }}
                    error={errors.phoneNumber}
                  />
                  {validator.message(
                    'phoneNumber',
                    account.phoneNumber,
                    'phone',
                    {
                      className:
                        'text-pos-red text-left text-[13px] leading-[0.9rem] mt-[10px] ml-1',
                    },
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="address" className="text-xbase block">
                  Address
                </label>
                <Input
                  id="address"
                  name="address"
                  value={account.address}
                  onChange={handleAccountInputChange}
                />
              </div>

              <div className="w-full">
                <label htmlFor="notes" className="text-xbase block mb-1">
                  Notes
                </label>
                <TextBox
                  id="notes"
                  name="notes"
                  value={account.notes}
                  onChange={handleAccountInputChange}
                />
              </div>
            </div>
            <div className="mt-4 text-center flex flex-row ml-auto space-x-4">
              <Button
                label="Save"
                onClick={handleCustomerSave}
                type="button"
                disabled={status === 'LOADING'}
                loading={status === 'LOADING'}
                className="!bg-pos-green border border-[rgba(27,31,36,0.15)] min-w-[150px]  h-[40px]
                      items-center justify-center text-white text-xbase
                      shadow-[0_1px_0_rgba(27,31,36,0.1),inset_0_1px_0_rgba(255,255,255,0.03)] shrink-0 font-medium"
              />
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default CustomerAdd;
